import { useEffect } from 'react';
import { Card, Avatar, List, Badge, Typography, Space, Tooltip, Row, Col, Statistic, Tabs } from 'antd';
import { useAppDispatch, useAppSelector } from '../methods/redux/hooks';
import { retrieveOnlineAdmins, OnlineAdmin } from '../methods/redux/reducers/roles';
import { ClockCircleOutlined, TeamOutlined, ThunderboltOutlined, HistoryOutlined } from '@ant-design/icons';

const { Text } = Typography;

const OnlineAdmins = () => {
    const dispatch = useAppDispatch();
    const { onlineAdmins } = useAppSelector((state) => state.roles);
    
    useEffect(() => {
        dispatch(retrieveOnlineAdmins());
        const interval = setInterval(() => {
            dispatch(retrieveOnlineAdmins());
        }, 60000);
        return () => clearInterval(interval);
    }, [dispatch]);

    const renderAdminList = (admins: OnlineAdmin[], type: 'online' | 'away' | 'offline') => {
        if (!admins || admins.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '40px 0', opacity: 0.5 }}>
                    <TeamOutlined style={{ fontSize: '48px', color: '#f5f5f5', marginBottom: '16px' }} />
                    <br />
                    <Text type="secondary">No admins in this category.</Text>
                </div>
            );
        }

        return (
            <List
                itemLayout="horizontal"
                dataSource={admins}
                renderItem={(item: OnlineAdmin) => (
                    <List.Item 
                        style={{ 
                            padding: '12px 16px', 
                            borderRadius: '12px', 
                            marginBottom: '8px',
                            background: type === 'online' ? 'rgba(82, 196, 26, 0.02)' : 'transparent',
                            border: '1px solid transparent',
                            transition: 'all 0.3s ease'
                        }}
                        className="admin-list-item"
                    >
                        <List.Item.Meta
                            avatar={
                                <Badge 
                                    dot 
                                    color={type === 'online' ? '#52c41a' : type === 'away' ? '#faad14' : '#bfbfbf'} 
                                    offset={[-2, 32]}
                                >
                                    <Avatar 
                                        size={44} 
                                        src={item.admin.profileUrl || item.admin.profileImageUrl}
                                        style={{ 
                                            backgroundColor: type === 'online' ? '#52c41a' : '#1890ff',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        {item.admin.fullName?.[0]?.toUpperCase() || item.admin.email?.[0]?.toUpperCase()}
                                    </Avatar>
                                </Badge>
                            }
                            title={
                                <Space direction="horizontal" style={{ width: '100%', justifyContent: 'space-between' }}>
                                    <Text strong style={{ fontSize: '14px' }}>{item.admin.fullName || 'Unnamed Admin'}</Text>
                                    <Text type="secondary" style={{ fontSize: '10px', textTransform: 'uppercase' }}>
                                        {item.admin.role?.title || 'No Role'}
                                    </Text>
                                </Space>
                            }
                            description={
                                <div style={{ marginTop: '2px' }}>
                                    <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>
                                        {item.admin.email}
                                    </Text>
                                    <Space size={12} style={{ marginTop: '4px' }}>
                                        <Space size={4}>
                                            <ClockCircleOutlined style={{ fontSize: '10px', color: '#bfbfbf' }} />
                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                {item.lastSeen}
                                            </Text>
                                        </Space>
                                        {item.ipAddress && (
                                            <Tooltip title={`IP: ${item.ipAddress}`}>
                                                <Space size={4}>
                                                    <ThunderboltOutlined style={{ fontSize: '10px', color: '#bfbfbf' }} />
                                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                                        {item.countryCode || 'Unknown'}
                                                    </Text>
                                                </Space>
                                            </Tooltip>
                                        )}
                                    </Space>
                                </div>
                            }
                        />
                    </List.Item>
                )}
            />
        );
    };

    return (
        <Card 
            title={
                <Space>
                    <TeamOutlined style={{ color: '#1890ff' }} />
                    <span style={{ fontWeight: 600 }}>Admin Activity</span>
                </Space>
            }
            extra={
                <Badge 
                    count={`Total: ${onlineAdmins.totalAdmins}`} 
                    style={{ backgroundColor: '#f5f5f5', color: '#8c8c8c', boxShadow: 'none', border: '1px solid #d9d9d9' }} 
                />
            }
            style={{ 
                borderRadius: '20px', 
                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                minHeight: '520px',
                border: '1px solid #f0f0f0',
                overflow: 'hidden'
            }}
            bodyStyle={{ padding: '0px' }}
        >
            <div style={{ padding: '20px 24px 10px 24px', background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)' }}>
                <Row gutter={16}>
                    <Col span={12}>
                        <div style={{ background: '#fff', padding: '12px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                            <Statistic 
                                title={<Text type="secondary" style={{ fontSize: '12px' }}>Online (5m)</Text>} 
                                value={onlineAdmins.totalOnlineLast5Minutes} 
                                valueStyle={{ color: '#52c41a', fontWeight: '700', fontSize: '20px' }}
                                prefix={<ThunderboltOutlined style={{ fontSize: '14px' }} />}
                            />
                        </div>
                    </Col>
                    <Col span={12}>
                        <div style={{ background: '#fff', padding: '12px', borderRadius: '12px', border: '1px solid #f0f0f0' }}>
                            <Statistic 
                                title={<Text type="secondary" style={{ fontSize: '12px' }}>Active (30m)</Text>} 
                                value={onlineAdmins.totalOnlineLast30Minutes} 
                                valueStyle={{ color: '#1890ff', fontWeight: '700', fontSize: '20px' }}
                                prefix={<HistoryOutlined style={{ fontSize: '14px' }} />}
                            />
                        </div>
                    </Col>
                </Row>
            </div>

            <div style={{ padding: '0 24px 24px 24px' }}>
                <Tabs 
                    defaultActiveKey="1" 
                    items={[
                        {
                            key: '1',
                            label: (
                                <span>
                                    Active <Badge count={onlineAdmins.onlineLast5Minutes?.length} offset={[10, -2]} size="small" style={{ backgroundColor: '#52c41a' }} />
                                </span>
                            ),
                            children: (
                                <div style={{ maxHeight: '320px', overflowY: 'auto', paddingRight: '4px' }}>
                                    {renderAdminList(onlineAdmins.onlineLast5Minutes, 'online')}
                                </div>
                            ),
                        },
                        {
                            key: '2',
                            label: (
                                <span>
                                    Away <Badge count={onlineAdmins.onlineLast30Minutes?.length} offset={[10, -2]} size="small" style={{ backgroundColor: '#1890ff' }} />
                                </span>
                            ),
                            children: (
                                <div style={{ maxHeight: '320px', overflowY: 'auto', paddingRight: '4px' }}>
                                    {renderAdminList(onlineAdmins.onlineLast30Minutes, 'away')}
                                </div>
                            ),
                        },
                        {
                            key: '3',
                            label: (
                                <span>
                                    Offline <Badge count={onlineAdmins.offlineAdmins?.length} offset={[10, -2]} size="small" style={{ backgroundColor: '#bfbfbf' }} />
                                </span>
                            ),
                            children: (
                                <div style={{ maxHeight: '320px', overflowY: 'auto', paddingRight: '4px' }}>
                                    {renderAdminList(onlineAdmins.offlineAdmins, 'offline')}
                                </div>
                            ),
                        },
                    ]}
                    style={{ marginTop: '8px' }}
                />
            </div>
        </Card>
    );
};

export default OnlineAdmins;
